import React, { createContext } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import SubscriptionServices from './Services/Consumers/SubscriptionServices'
import { NamedRoutes } from './routes'
import { read, utils } from 'xlsx'

const UserGroupContext = createContext({})
const UserGroupProvider = ({ children, userGroupId, isEdit, handleNotificationCall }) => {
  const { keycloak } = useKeycloak()
  const intl = useIntl()
  const history = useHistory()

  const [userGroupData, setUserGroupData] = React.useState({})
  const [isLoading, setIsLoading] = React.useState(false)
  const [userGroupDataFailure, setUserGroupDataFailure] = React.useState(false)
  const [bulkImportHasError, setBulkImportHasError] = React.useState(false)

  React.useEffect(() => {
    if (isEdit && !!userGroupId && !!parseInt(userGroupId)) {
      getUserGroupData()
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const getUserGroupData = () => {
    if (!!keycloak?.authenticated) {
      setIsLoading(true)
      setUserGroupDataFailure(false)
      SubscriptionServices.getUserGroupData(userGroupId, {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }).then((result) => {
        if ((!!result && result.status !== 200) || !result) {
          setUserGroupDataFailure(true)
        }
        else {
          if (!!result && !!result.data && !!result.data.data) {
            setUserGroupData(result.data.data)
          } else {
            setUserGroupDataFailure(true)
          }
        }
      }).then(() => {
        setIsLoading(false)
      })
    }
  }

  const handleSubmit = (values, FormikActions) => {
    if (!!keycloak?.authenticated) {
      FormikActions.setSubmitting(true)
      SubscriptionServices.submitUserGroup(
        userGroupId,
        isEdit,
        values,
        { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } },
        null,
        null,
        FormikActions
      ).then((result) => {
        if (result && result.data && result.status === 200) {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({
              id: isEdit ? 'UserGroup.SuccessUpdate' : 'UserGroup.SuccessCreate'
            }),
            color: 'success'
          }, 'success')
          history.push(NamedRoutes.subscription.userGroups.list)
        } else {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response && result.response.data) {
            if (result.response.data && result.response.data.status && result.response.data.status.errors) {
              message = result.response.data.status.errors
            }
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        }
      })
    }
  }

  const handleBulkImportSubmit = async (values, FormikActions, closeDialog, reloadPage) => {
    if (!!keycloak?.authenticated) {
      try {
        const file = values.userGroupsFile

        const fileBuffer = await file.arrayBuffer()
        const workbook = read(fileBuffer)
        const worksheet = workbook.Sheets[workbook.SheetNames[0]]
        const data = utils.sheet_to_json(worksheet)

        if (!data.length) {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({ id: 'UserGroupBulkImport.file.empty' }),
            color: 'error'
          }, 'error')
          setTimeout(() => {
            closeDialog()
          }, 500)
          return
        }

        const payloadRows = data.map(row => ({
          name: row['name'],
          keycloak_user_ids: row['keycloak_user_ids']?.split(';').map(id => id.trim())
        }))

        const bulkImportPayload = { data: payloadRows }

        const result = await SubscriptionServices.bulkImportUserGroups(
          bulkImportPayload,
          { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } },
          null,
          null,
          FormikActions
        )

        if (result && result.data && result.status === 200) {
          const count = result.data.data?.created || result.data.data?.updated || 0
          setBulkImportHasError(false)
          if (!!count) {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'UserGroupBulkImport.success.message' }, { count }),
              color: 'success'
            }, 'success')
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'UserGroupBulkImport.noData' }),
              color: 'error'
            }, 'error')
          }
          FormikActions.setSubmitting(false)
          FormikActions.resetForm()
          // Close dialog and reload page
          closeDialog()
          reloadPage(true)
        } else {
          throw new Error(result?.response?.data?.status?.errors || result?.response?.data?.status?.message || intl.formatMessage({ id: 'bulkImport.failed' }))
        }
      } catch (error) {
        setBulkImportHasError(true)
        handleNotificationCall({
          type: 'defence',
          message: error.message || intl.formatMessage({ id: 'bulkImport.error' }),
          color: 'error'
        }, 'error')
        FormikActions.setSubmitting(false)
        FormikActions.resetForm()
      }
    }
  }

  const clearBulkImportError = () => {
    setBulkImportHasError(false)
  }

  return (
    <UserGroupContext.Provider value={{
      userGroupData,
      isLoading,
      isEdit,
      userGroupDataFailure,
      bulkImportHasError,

      handleSubmit,
      handleBulkImportSubmit,
      clearBulkImportError
    }}>
      {children}
    </UserGroupContext.Provider>
  )

}

const UserGroupConsumer = UserGroupContext.Consumer

export { UserGroupProvider, UserGroupConsumer }